import React, { useRef, useState, useEffect, forwardRef } from 'react';
import { sortBy, get } from 'lodash';
import moment from 'moment';
import { CheckboxCell } from './checkboxCell';
import { useDeal } from './context';
import { DealObjects } from './dealObjects';
import AssignModal from './assignModal';
import { State, ActionHandler, Dropdown, Menu, IconButton, Tooltip } from '@components';
import {
  ArrowsUpDownIcon,
  PaperClipIcon,
  ArrowUpOnSquareIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  EyeIcon,
  ChatBubbleBottomCenterTextIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { deadlineClasses } from '../utils/deadlines';
import { useLongPress } from '../utils/longpress';
import { BASE_HEIGHT_CLASS, BASE_HEIGHT_CELL_CLASS } from '../utils/constants';
import { RenderAvatars, RenderAvatar } from '../utils/userAvatar';
import { RowMiscButton } from '../utils/rowButton';
import ExpiryColumn from './taskItemColumns/expiryColumn';
import EndAtColumn from './taskItemColumns/endAtColumn';

interface TaskItem {
  id: string;
  label: string;
  // Add other task item properties as needed
}

interface TaskItemProps {
  classes?: string;
  taskItemId: string;
}

interface TaskContext {
  taskItemById: Record<string, TaskItem>;
  selectedItemIds: string[];
  setSelectedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
}

export const SortKnob = forwardRef(({ ringClass = '' }, ref) => {
  return (
    <div
      className={`handle flex w-4 h-4 shadow rounded items-center justify-center ring-1 bg-white left-12 top-2 !p-0 z-[2] ring-1 ring-slate-200 cursor-grab`}
    >
      <ArrowsUpDownIcon className="w-3 h-3" />
    </div>
  );
});

function Clickable({ children, taskItemId, className }) {
  return (
    <ActionHandler
      className={className}
      href={`/task_items/${taskItemId}`}
      data={{ turbo_frame: 'dialog', turbo_method: 'get', turbo_stream: true }}
    >
      {children}
    </ActionHandler>
  );
}

function Activity({ activity }) {
  const date = moment(activity.created_at).format('D/MM/YYYY');
  return (
    <div className="py-1 px-3 text-[11px] flex flex-col">
      <div
        className="[&_span]:font-hmedium truncate [&_span]:truncate"
        dangerouslySetInnerHTML={{ __html: `${activity.initiator_label} ${activity.label}` }}
      />
      <span className="text-[10px] text-slate-400">{date}</span>
    </div>
  );
}

export const TaskItem = forwardRef<HTMLDivElement, TaskItemProps>(
  ({ classes = '', taskItemId }, ref) => {
    const {
      actions = {},
      refreshing,
      taskItemById,
      selectedItemIds,
      setSelectedItemIds,
      availableStates,
      deal,
      dealObjects,
      portalId,
      authStatuses
    } = useDeal() as TaskContext;

    const taskItem = get(taskItemById, taskItemId) as TaskItem;
    const [menuIsOpened, setMenuIsOpened] = useState(false);
    const [stateIsOpened, setStateIsOpened] = useState(false);
    const [objectsIsOpened, setObjectsIsOpened] = useState(false);
    const objects = get(dealObjects, get(taskItem, 'target_type'), []);
    const trackable = objects.find(object => Number(object.id) === Number(taskItem.target_id));
    const trackableLabel = get(trackable, 'label');
    const trackableSubtitle = get(trackable, 'subtitle');
    const [showEditorAssignModal, setShowEditorAssignModal] = useState(false);
    const [showViewerAssignModal, setShowViewerAssignModal] = useState(false);

    const inputRef = useRef();
    const [label, setLabel] = useState(get(taskItem, 'label') || '');
    const [editableLabel, setEditableLabel] = useState(false);
    const inputLongpress = useLongPress(() => {
      setEditableLabel(true);
    });

    useEffect(() => {
      if (!!editableLabel) {
        inputRef.current.focus();
      }
    }, [editableLabel, inputRef.current]);

    if (!taskItem) {
      return null;
    }

    // fields
    const filesCount = get(taskItem, 'files_count', 0) || 0;
    const currentState = availableStates.find(s => s.id === taskItem.template_status_id);

    const rawEndAt = get(taskItem, 'end_at');
    const endAt = rawEndAt ? moment(rawEndAt).format('D/M/YY') : null;
    const { bg: deadlineBgClasses, text: deadlineTextClasses } = rawEndAt
      ? deadlineClasses(rawEndAt, get(taskItem, 'state') === 'VALIDATED')
      : {};

    const toggleSelection = () => {
      setSelectedItemIds(prevItemIds => {
        if (prevItemIds.includes(taskItemId)) {
          return prevItemIds.filter(itemId => itemId !== taskItemId);
        }
        return [...prevItemIds, taskItemId];
      });
    };

    const onUpdate = data => {
      actions.updateTaskItem(taskItemId, data, 'La démarche a été mise à jour.');
    };

    const trackableBadge = (
      <RowMiscButton label={trackableLabel} icon={!trackableLabel && PencilSquareIcon} />
    );

    const trackableButton =
      !!trackableLabel && trackableLabel !== '' ? (
        <Tooltip
          content={[trackableLabel, trackableSubtitle ? `(${trackableSubtitle})` : ''].join(' ')}
        >
          {trackableBadge}
        </Tooltip>
      ) : (
        trackableBadge
      );

    return (
      <>
        <AssignModal
          title="Qui doit compléter?"
          subtitle="Sélectionner parmi les intervenants du dossier"
          accessType="editor"
          deal={deal}
          isOpen={showEditorAssignModal}
          onClose={() => setShowEditorAssignModal(false)}
          taskItemIds={[taskItem.id]}
          defaultTemplateSlug="AFFECTATION_DE_DEMARCHES"
        />

        <AssignModal
          title="Qui peut consulter ?"
          subtitle="Sélectionner parmi les intervenants du dossier"
          accessType="viewer"
          deal={deal}
          isOpen={showViewerAssignModal}
          onClose={() => setShowViewerAssignModal(false)}
          taskItemIds={[taskItem.id]}
          defaultTemplateSlug="PARTAGE_DE_DEMARCHES"
        />
        <div
          data-id={taskItemId}
          ref={ref}
          data-role="item"
          className="group/item relative group bh-white"
        >
          <div
            className={`${
              menuIsOpened ? 'block' : 'hidden group-hover/item:block'
            } absolute w-8 h-6 -left-8 top-1/2 -translate-y-1/2`}
          >
            <Dropdown
              isOpened={menuIsOpened}
              setIsOpened={setMenuIsOpened}
              hideOnClick={['[data-turbo-frame]']}
              button={<IconButton icon={EllipsisVerticalIcon} />}
            >
              <Menu
                className="w-[150px]"
                groups={[
                  {
                    options: [
                      {
                        label: 'Voir le détail',
                        icon: EyeIcon,
                        href: `/task_items/${taskItemId}`,
                        data: { turbo_frame: 'dialog', turbo_method: 'get', turbo_stream: true }
                      }
                    ]
                  },
                  {
                    options: [
                      {
                        label: 'Supprimer',
                        className: 'text-red-600',
                        icon: XMarkIcon,
                        confirm: {
                          title: 'Supprimer la démarche ?',
                          subtitle: 'Cette action est irréversible.',
                          loading: refreshing,
                          onConfirm: () =>
                            actions.deleteTaskItem(taskItemId, 'La démarche a été supprimée.')
                        }
                      }
                    ]
                  }
                ]}
              />
            </Dropdown>
          </div>
          <div
            className={`z-[2] hidden lg:group-hover/item:flex items-center pr-2 pl-2 absolute left-[40px] top-1/2 ${BASE_HEIGHT_CLASS} -translate-y-1/2`}
          >
            <SortKnob />
          </div>
          <div
            className={`hover:bg-slate-50 ${BASE_HEIGHT_CLASS} ${BASE_HEIGHT_CELL_CLASS} z-[1] grid ${classes} divide-x relative z-[1] select-none`}
          >
            <CheckboxCell
              checked={(selectedItemIds || []).includes(taskItemId)}
              onClick={toggleSelection}
            />
            <div className="flex items-center text-slate-700 text-sm font-hmedium space-x-3 [&>[data-target='label']]:w-full [&>>[data-target='label']]:flex-shrink">
              <div
                data-target="label"
                className="flex-shrink truncate"
                {...inputLongpress.handlers}
              >
                {editableLabel ? (
                  <form
                    className="w-full relative"
                    onSubmit={e => {
                      e.preventDefault();
                      if (label !== '') {
                        onUpdate({ label });
                        setEditableLabel(false);
                      }
                    }}
                  >
                    <input
                      className="w-full"
                      ref={inputRef}
                      type="text"
                      onBlur={e => {
                        if (e.target.value !== '') {
                          onUpdate({ label: e.target.value });
                        }
                        setEditableLabel(false);
                      }}
                      onChange={e => setLabel(e.target.value)}
                      value={label}
                    />
                    <input type="submit" className="absolute opacity-0" />
                  </form>
                ) : (
                  <Clickable className="flex-shrink truncate" taskItemId={taskItem.id}>
                    {label}
                  </Clickable>
                )}
              </div>
              <div className="flex-shrink-0 hidden lg:flex items-center space-x-2">
                {taskItem.notes_count > 0 && (
                  <ActionHandler
                    href={`/task_items/${taskItemId}/notes?deal_label=${deal.reference}&label=${taskItem.label}`}
                    data={{ turbo_frame: 'dialog', turbo_method: 'get', turbo_stream: true }}
                  >
                    <RowMiscButton
                      icon={ChatBubbleBottomCenterTextIcon}
                      color={taskItem.notes_count > 0 ? 'yellow' : 'slate'}
                    />
                  </ActionHandler>
                )}
                <Dropdown
                  isOpened={objectsIsOpened}
                  setIsOpened={setObjectsIsOpened}
                  button={trackableButton}
                >
                  <Menu className="max-h-[300px] w-[300px]">
                    <DealObjects
                      selectedTargetType={taskItem.target_type}
                      selectedTargetId={taskItem.target_id}
                      taskItemId={taskItemId}
                      onClickSuccess={() => {
                        setObjectsIsOpened(false);
                      }}
                    />
                  </Menu>
                </Dropdown>
              </div>
            </div>
            <div
              className={`cell !p-0 [&>a]:w-full [&>a]:flex [&>a]:justify-center hidden lg:inline-flex items-center justify-center relative ${
                taskItem.last_export_at ? 'bg-green-50' : ''
              }`}
            >
              {taskItem.last_export_at && (
                <div className="pointer-events-none top-0 left-0 absolute text-[8px] font-hmedium text-white bg-green-500 w-[36px] origin-top-right -translate-x-full -rotate-90 transform text-center">
                  Exporté
                </div>
              )}
              <Clickable taskItemId={taskItem.id}>
                {filesCount === 0 ? (
                  <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
                ) : (
                  <span
                    className={`flex items-center space-x-1 text-sm ${
                      taskItem.last_export_at ? 'text-green-600' : 'text-slate-400'
                    }`}
                  >
                    <PaperClipIcon className="w-4 h-4" />
                    <span className="font-hmedium">{filesCount}</span>
                  </span>
                )}
              </Clickable>
            </div>

            <ExpiryColumn taskItem={taskItem} onUpdate={onUpdate} filesCount={filesCount} />

            <div className="cell !p-0 relative overflow-hidden overflow-hidden [&>div]:w-full [&>div>button]:w-full [&>div]:overflow-hidden [&>div>button]:overflow-hidden">
              <Dropdown
                isOpened={stateIsOpened}
                setIsOpened={setStateIsOpened}
                button={
                  <State
                    height={`${BASE_HEIGHT_CLASS} w-full`}
                    label={get(currentState, 'label')}
                    color={get(currentState, 'color')}
                  />
                }
              >
                <Menu className="w-[150px]">
                  <div className="p-2 flex flex-col space-y-[1px]">
                    {sortBy(availableStates, 'label').map(availableState => (
                      <State
                        height={BASE_HEIGHT_CLASS}
                        absolute={false}
                        key={availableState.id}
                        {...availableState}
                        onClick={() => {
                          onUpdate({ template_status_id: availableState.id });
                          setStateIsOpened(false);
                        }}
                      />
                    ))}
                  </div>
                </Menu>
              </Dropdown>
            </div>
            <EndAtColumn taskItem={taskItem} onUpdate={onUpdate} />
            <div className="cell [&>a]:w-full hidden lg:flex !p-0 [&>a]:flex [&>a]:w-full [&>a]:flex [&>a]:items-center [&>a]:justify-center">
              <button
                className="w-full flex justify-center items-center"
                onClick={() => setShowEditorAssignModal(true)}
              >
                <RenderAvatars users={taskItem.editors} authStatuses={authStatuses} />
              </button>
            </div>
            <div className="cell [&>a]:w-full hidden lg:flex !p-0 [&>a]:w-full [&>a]:items-center [&>a]:flex [&>a]:justify-center">
              <button
                className="w-full flex justify-center items-center"
                onClick={() => setShowViewerAssignModal(true)}
              >
                <RenderAvatars users={taskItem.viewers} authStatuses={authStatuses} />
              </button>
            </div>
            <div className="cell [&>div]:w-full hidden lg:flex items-center justify-center [&>div]:flex [&>div]:items-center [&>div]:justify-center !p-0">
              <RenderAvatar user={taskItem.initiator} authStatuses={authStatuses} />
            </div>
          </div>
        </div>
      </>
    );
  }
);
